import { IconButton, List, useMediaQuery } from '@mui/material'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    useCreateWorkplaceApiV1CollaborationAppWorkplacePost,
    useWorkplaceListApiV1CollaborationAppWorkplaceGet,
    WorkplaceDTO,
} from '../../../../api/gen/base'
import DotsHorizontalIcon from '../../../../assets/icons/DotsHorizontal.svg?react'
import PlusIcon from '../../../../assets/icons/Plus.svg'
import { PROJECT_ROUTE } from '../../../../constants/routing.constants'
import { ProjectCreateDialog } from '../../../../dialogs/ProjectCreateDialog/ProjectCreateDialog'
import { useWrapperStore } from '../../../../store/storeWrapper'
import { theme } from '../../../../theme'
import { NavigationButton } from './NavigationButton'
import { NavigationGroupTitle } from './NavigationGroupTitle'
import { ProjectsAccordion } from './ProjectsAccordion'
import { SidebarMenu } from './SidebarMenu'
import { SidebarTooltip } from './SidebarTooltip'

type ProjectsState = {
    latest: WorkplaceDTO[]
    other: WorkplaceDTO[]
    total: number
}

export const ProjectsList: FC = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const navigate = useNavigate()
    const [openProjectCreateDialog, setOpenProjectCreateDialog] = useState<boolean>(false)
    const { isTempUser, fetchProjectList, setOpenSidebar, stepOnboarding } = useWrapperStore(
        (state) => state,
    )

    const handleCloseProjectCreateDialog = () => {
        setOpenProjectCreateDialog(false)
    }

    const {
        data: projectListData,
        refetch: refetchProjects,
        isLoading: isLoadingProjects,
    } = useWorkplaceListApiV1CollaborationAppWorkplaceGet({
        limit: 100,
    })
    const {
        data: createProjectData,
        mutate: createProject,
        isPending: isCreatingProject,
    } = useCreateWorkplaceApiV1CollaborationAppWorkplacePost()

    const [projects, setProjects] = useState<ProjectsState>({ latest: [], other: [], total: 0 })

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleOpenMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()

        setAnchorEl(event.currentTarget)
    }
    const handleCloseMenu = () => {
        setAnchorEl(null)
    }

    const handleCreateProject = () => {
        setOpenProjectCreateDialog(true)
    }

    useEffect(() => {
        if (projectListData) {
            const projects = projectListData.objects.filter(({ is_archived }) => !is_archived)

            setProjects({
                latest: projects.slice(0, 3),
                other: projects.slice(3),
                total: projects.length,
            })
        }
    }, [projectListData])

    useEffect(() => {
        if (createProjectData) {
            refetchProjects()

            // TODO: удалить когда сделаем рефакторинг для хранилища (выпилим запросы)
            fetchProjectList({ limit: 100 })
        }
    }, [createProjectData])

    const handleOpenProjectPage = useCallback(
        (projectId: string) => {
            navigate(`${PROJECT_ROUTE}/?workplaceId=${projectId}&is_archived=false`)
        },
        [navigate],
    )

    const menuItems = useMemo(
        () =>
            projects.other.map(({ id, title }) => ({
                label: title,
                key: id,
                onClick: () => {
                    handleOpenProjectPage(id)
                    setOpenSidebar(false)
                },
            })),
        [projects, handleOpenProjectPage],
    )

    return (
        <List
            sx={{
                // pointerEvents: stepOnboarding === 100 ? 'auto':  'none' ,
                p: '0 16px',
                zIndex: (theme) =>
                    !isMobile && stepOnboarding === 4 ? theme.zIndex.drawer + 1 : 0,
            }}>
            <NavigationGroupTitle
                endButton={
                    <SidebarTooltip title="Создать новый проект">
                        <IconButton
                            onClick={handleCreateProject}
                            disabled={
                                isCreatingProject ||
                                isLoadingProjects ||
                                isTempUser ||
                                stepOnboarding !== 100
                            }>
                            <PlusIcon />
                        </IconButton>
                    </SidebarTooltip>
                }>
                Проекты
            </NavigationGroupTitle>
            {projects.latest.map(({ id, title, is_protected }, index) => (
                <ProjectsAccordion
                    index={index}
                    is_protected={is_protected}
                    key={id}
                    title={title}
                    projectId={id}
                    onOpenProject={handleOpenProjectPage}
                />
            ))}
            {projects.latest.length < projects.total && (
                <NavigationButton
                    onClick={handleOpenMenu}
                    textColor="#5A5F72"
                    startIcon={<DotsHorizontalIcon />}
                    shouldCloseSidebar={false}>
                    Показать все
                </NavigationButton>
            )}
            <SidebarMenu
                verticalPosition="center"
                anchorEl={anchorEl}
                menuItems={menuItems}
                onClose={handleCloseMenu}
            />
            <ProjectCreateDialog
                open={openProjectCreateDialog}
                handleClose={handleCloseProjectCreateDialog}
                createProject={createProject}
            />
        </List>
    )
}
